import { Deserializable } from '../protocols/deserializable';
import { NameChangeSubmission } from './name-change-submission';
import { SubmissionComment } from './submission-comment';
import { MemberSubmissionTypeEnum } from '../users/enum/member-submission-type-enum';
import { SubmissionAttachment } from './submission-attachment';
import { LookAheadItem } from '../../views/shared/components/search-with-look-ahead/look-ahead-list/look-ahead-item/protocol/look-ahead-item';
import { Searchable } from '../protocols/searchable';
import { AddressChangeSubmission } from './address-change-submission';
import { ContactInfoChangeSubmission } from './contact-info-change-submission';
import { SubmissionHistoryItem } from './submission-history-item';
import { Mentionable } from '../base/mentionable';
import { PersonalInfoSubmission } from './personal-info-submission';
import { PortalTypeIdEnum } from '../enum/shared/portal-type-id.enum';
import { SubmissionStatusPill } from '../shared/submission-status-pill';
import * as moment from 'moment/moment';
import { SubmissionStatusIdEnum } from '../enum/shared/submission-status-id.enum';
import { SubmissionSubStatusIdEnum } from '../enum/shared/submission-sub-status-id-enum';
import { SubmissionSubStatus } from './submission-sub-status';
import { MemberSubmissionStatus } from './member-submission-status';
import { InvestmentInstructionSubmission } from './investment-instruction-submission';
import { RelationshipStatusSubmission } from './relationship-status-submission';
import { BeneficiaryChangeSubmission } from './beneficiary-change-submission';
import { BankingInformationSubmission } from './banking-information-submission';
import { UpdateLegalFiduciarySubmission } from './update-legal-fiduciary-submission';
import { VariableBenefitConfigurationSubmission } from './variable-benefit-configuration-submission';
import { UpdateVariableBenefitConfigurationSubmission } from './update-variable-benefit-configuration-submission';
import { VariableBenefitPaymentChangeSubmission } from './variable-benefit-payment-change-submission';
import { MonthlyPensionSetupSubmission } from './monthly-pension-setup-submission';
import { PaylineAccountSubmission } from './payline-account-submission';
import { TransferInSubmission } from './transfer-in-submission';

export class MemberSubmission implements Deserializable {
  // TODO: Tidy this up once all submission types are added to this object
  public id!: number;
  public memberId!: number;
  public memberName!: string;
  public submissionTypeId!: MemberSubmissionTypeEnum;
  public submissionTypeName!: string;
  public statusId!: SubmissionStatusIdEnum;
  public status!: MemberSubmissionStatus;
  public subStatusId!: SubmissionSubStatusIdEnum | null;
  public subStatus!: SubmissionSubStatus;
  public comments!: SubmissionComment[];
  public owner!: SubmissionStakeholder;
  public ownerId!: number | null;
  public approver!: SubmissionStakeholder;
  public approverId!: number | null;
  public attachments!: SubmissionAttachment[];
  public history!: SubmissionHistoryItem[];
  public idPrefix!: string;
  public idSuffix!: string;
  public submittedDate!: string;
  public overdueDate!: string;
  public followUpDate!: string | null;
  public nameChangeSubmissionId!: string | null;
  public addressChangeSubmissionId!: string | null;
  public updateContactInformationSubmissionId!: string | null;
  public personalInformationSubmissionId!: string | null;
  public investmentInstructionsSubmissionId!: string | null;
  public relationshipStatusSubmissionId!: string | null;
  public bankingInformationSubmissionId!: string | null;
  public legalFiduciarySubmissionId!: string | null;
  public variableBenefitConfigurationSubmissionId!: string | null;
  public updateVariableBenefitConfigurationSubmissionId!: string | null;
  public variableBenefitPaymentChangeSubmissionId!: string | null;
  public monthlyPensionSetupSubmissionId!: string | null;
  public beneficiaryChangeSubmission!: BeneficiaryChangeSubmission | null;
  public nameChangeSubmission!: NameChangeSubmission | null;
  public contactInformationSubmission!: ContactInfoChangeSubmission | null;
  public addressChangeSubmission!: AddressChangeSubmission | null;
  public personalInformationSubmission!: PersonalInfoSubmission | null;
  public investmentInstructionSubmission!: InvestmentInstructionSubmission | null;
  public relationshipStatusSubmission!: RelationshipStatusSubmission | null;
  public bankingInformationSubmission!: BankingInformationSubmission | null;
  public legalFiduciarySubmission!: UpdateLegalFiduciarySubmission | null;
  public variableBenefitConfigurationSubmission!: VariableBenefitConfigurationSubmission | null;
  public updateVariableBenefitConfigurationSubmission!: UpdateVariableBenefitConfigurationSubmission | null;
  public variableBenefitPaymentChangeSubmission!: VariableBenefitPaymentChangeSubmission | null;
  public monthlyPensionSetupSubmission!: MonthlyPensionSetupSubmission | null;
  public paylineAccountSubmission!: PaylineAccountSubmission | null;
  public transferInSubmission!: TransferInSubmission | null;
  public reminder!: SubmissionReminder | null;
  public expirationDate!: string | null;
  public lastStatusUpdateDate!: string | null;
  public createdInPortalTypeId!: PortalTypeIdEnum;
  public completedDate!: string | null;
  public createdDate!: string | null;

  // Not From API (yet)
  public submissionId!: string;
  public formattedCreatedDate!: string;
  public formattedCompletedDate!: string;
  public formattedExpirationDate!: string;
  public expiringSoon!: boolean;
  public statusPill!: SubmissionStatusPill;

  public canChangeSubmissionOwner(): boolean {
    return (
      this.subStatusId === SubmissionSubStatusIdEnum.Ready ||
      this.subStatusId === SubmissionSubStatusIdEnum.PendingDocuments ||
      this.subStatusId === SubmissionSubStatusIdEnum.Processing ||
      this.subStatusId === SubmissionSubStatusIdEnum.MemberActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.EmployerActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval
    );
  }

  public canChangeSubmissionApprover(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval;
  }

  public canUploadFiles(): boolean {
    return this.statusId === SubmissionStatusIdEnum.Submitted || this.statusId === SubmissionStatusIdEnum.InReview;
  }

  public isApproved(): boolean {
    // @ts-ignore
    return [SubmissionSubStatusIdEnum.Approved, SubmissionSubStatusIdEnum.AutoApproved].contains(this.subStatusId ?? 0);
  }

  public isRejected(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.Rejected;
  }

  public isActive(): boolean {
    return [SubmissionStatusIdEnum.InReview, SubmissionStatusIdEnum.Submitted, SubmissionStatusIdEnum.Pending].contains(
      this.status.id
    );
  }

  onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    this.submissionId = this.buildSubmissionId();
    if (!!this.owner) {
      this.owner = Deserialize?.instanceOf(SubmissionStakeholder, this.owner);
    }
    if (!!this.approver) {
      this.approver = Deserialize?.instanceOf(SubmissionStakeholder, this.approver);
    }
    if (!!this.comments) {
      this.comments = Deserialize?.arrayOf(SubmissionComment, this.comments);
    }
    if (!!this.reminder) {
      this.reminder = Deserialize?.instanceOf(SubmissionReminder, this.reminder);
    }
    if (!!this.history) {
      this.history = Deserialize?.arrayOf(SubmissionHistoryItem, this.history);
      this.history.sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));
    }
    if (!!this.status) {
      this.status = Deserialize?.instanceOf(MemberSubmissionStatus, this.status);
    }
    if (!!this.subStatus) {
      this.subStatus = Deserialize?.instanceOf(SubmissionSubStatus, this.subStatus);
    }
    if (!!this.createdDate) {
      this.formattedCreatedDate = moment(this.createdDate).format('MMMM D, YYYY');
    }
    if (!!this.expirationDate) {
      this.formattedExpirationDate = moment(this.expirationDate).format('MMMM D, YYYY');
      // if the submission is expiring within the next 10 days, set expiringSoon to true
      this.expiringSoon = moment(this.expirationDate).diff(moment(), 'days') <= 10;
    }
    if (!!this.completedDate) {
      this.formattedCompletedDate = moment(this.completedDate).format('DD / MM / YYYY');
    }
    if (!!this.status) {
      const statusName = this.isRejected() ? $localize`Rejected` : this.status.name;
      this.statusPill = new SubmissionStatusPill(this.id, statusName, this.statusId, this.status.name);
    }
    if (!!this.nameChangeSubmission) {
      this.nameChangeSubmission = Deserialize?.instanceOf(NameChangeSubmission, this.nameChangeSubmission);
    }
    if (!!this.contactInformationSubmission) {
      this.contactInformationSubmission = Deserialize?.instanceOf(
        ContactInfoChangeSubmission,
        this.contactInformationSubmission
      );
    }
    if (!!this.addressChangeSubmission) {
      this.addressChangeSubmission = Deserialize?.instanceOf(AddressChangeSubmission, this.addressChangeSubmission);
    }
    if (!!this.investmentInstructionSubmission) {
      this.investmentInstructionSubmission = Deserialize?.instanceOf(
        InvestmentInstructionSubmission,
        this.investmentInstructionSubmission
      );
    }
    if (!!this.personalInformationSubmission) {
      this.personalInformationSubmission = Deserialize?.instanceOf(
        PersonalInfoSubmission,
        this.personalInformationSubmission
      );
    }
    if (!!this.relationshipStatusSubmission) {
      this.relationshipStatusSubmission = Deserialize?.instanceOf(
        RelationshipStatusSubmission,
        this.relationshipStatusSubmission
      );
    }
    if (!!this.beneficiaryChangeSubmission) {
      this.beneficiaryChangeSubmission = Deserialize?.instanceOf(
        BeneficiaryChangeSubmission,
        this.beneficiaryChangeSubmission
      );
    }
    if (!!this.bankingInformationSubmission) {
      this.bankingInformationSubmission = Deserialize?.instanceOf(
        BankingInformationSubmission,
        this.bankingInformationSubmission
      );
    }
    if (!!this.legalFiduciarySubmission) {
      this.legalFiduciarySubmission = Deserialize?.instanceOf(
        UpdateLegalFiduciarySubmission,
        this.legalFiduciarySubmission
      );
    }
    if (this.variableBenefitConfigurationSubmission) {
      this.variableBenefitConfigurationSubmission = Deserialize?.instanceOf(
        VariableBenefitConfigurationSubmission,
        this.variableBenefitConfigurationSubmission
      );
    }
    if (this.updateVariableBenefitConfigurationSubmission) {
      this.updateVariableBenefitConfigurationSubmission = Deserialize?.instanceOf(
        UpdateVariableBenefitConfigurationSubmission,
        this.updateVariableBenefitConfigurationSubmission
      );
    }
    if (this.variableBenefitPaymentChangeSubmission) {
      this.variableBenefitPaymentChangeSubmission = Deserialize?.instanceOf(
        VariableBenefitPaymentChangeSubmission,
        this.variableBenefitPaymentChangeSubmission
      );
    }
    if (this.monthlyPensionSetupSubmission) {
      this.monthlyPensionSetupSubmission = Deserialize?.instanceOf(
        MonthlyPensionSetupSubmission,
        this.monthlyPensionSetupSubmission
      );
    }
    if (this.paylineAccountSubmission) {
      this.paylineAccountSubmission = Deserialize?.instanceOf(PaylineAccountSubmission, this.paylineAccountSubmission);
    }
    if (this.transferInSubmission) {
      this.transferInSubmission = Deserialize?.instanceOf(TransferInSubmission, this.transferInSubmission);
    }
    if (!!this.attachments) {
      this.attachments = Deserialize?.arrayOf(SubmissionAttachment, this.attachments);
    }
  }

  private buildSubmissionId(): string {
    // Submission IDs should be 8 characters long, padded with leading zeros
    const paddedId = this.id.toString().padStart(8, '0');
    return `${this.idPrefix}-${paddedId}-${this.idSuffix}`;
  }
}

export class SubmissionStakeholder extends Mentionable implements LookAheadItem, Searchable {
  // Searchable
  public title!: string;
  public value: any;

  onDeserialize() {
    super.onDeserialize();
    this.title = this.fullName;
    this.value = this.id;
  }

  lookAheadDisabled(): boolean {
    return false;
  }

  lookAheadDisabledMessage(): string {
    return '';
  }
}

export class SubmissionReminder implements Deserializable {
  public id!: number;
  public internalUserId!: number;
  public date!: string | null;

  onDeserialize() {}
}
